import React from 'react';
import {getGridFrameLayoutCSS} from './getGridFrameLayoutCSS';

export interface PrimitiveFrameLayoutProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
> {
  width: number;
  orientation: 'vertical' | 'horizontal';
  gap: number;
  /**
   * When > 1, lays frames out as a 2D grid with this many tracks on the
   * single axis perpendicular to `orientation`.
   */
  crossAxisTrackCount?: number;
  /**
   * Breakpoint container-name chain declared on the layout element. Omit to
   * keep the layout an unnamed inline-size container — used when frames name
   * their own breakpoint containers (multi-breakpoint mode, CANV-779).
   */
  breakpointContainerName?: string;
  filter?: string;
  children: React.ReactNode;
}

/**
 * A pure, data-source-agnostic React component that renders the CSS grid
 * container for laying out Frame elements.
 *
 * This is the shared primitive used by both the Component Canvas (via the
 * FrameLayout Atom) and the Open Canvas (via store-driven page frames).
 *
 * Note: FrameMeasurer is intentionally not included here — it stays in the
 * FrameLayout Atom wrapper where it manages measurement context.
 */
export const PrimitiveFrameLayout = React.forwardRef<
  HTMLDivElement,
  PrimitiveFrameLayoutProps
>(function PrimitiveFrameLayout(
  {
    width,
    orientation,
    gap,
    crossAxisTrackCount,
    breakpointContainerName,
    filter,
    children,
    style,
    ...rest
  },
  ref
) {
  const gridStyle = {
    ...getGridFrameLayoutCSS(orientation, gap, crossAxisTrackCount),
    gridAutoColumns: 'min-content',
    width,
    position: 'relative' as const,
    container: `${breakpointContainerName ?? 'none'} / inline-size`,
    filter,
    ...style,
  };

  return (
    <div
      data-automation-id="frame-layout"
      style={gridStyle}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});
